import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import {
  setAdHocDates,
  changeRate,
  setIsHoliday,
  saveDatesForBooking,
  changeStartDate,
  changeEndDate,
} from '../../../features/bookingForm/bookingFormSlice';
import Calendar from '../Calendar/Calendar';
import JobSectionFooter from '../JobSectionFooter';
import Input from '../../Input/Input';
import Select from '../../Select/Select';
import delete_icon from '../../../assets/images/delete.png';
import split_icon from '../../../assets/images/split.png';
import css from '../Standard/Standard.module.css';
import localCss from './AdHoc.module.css';
import { IMaskInput } from 'react-imask';
import inputCss from '../../Input/Input.module.css';

const momentFormat = 'HH:mm';

const AdHoc = ({ isAvailability }) => {
  const [hint, setHint] = useState(false);
  const [firstTime, setFirstTime] = useState(true);
  const [disableConfirm, setDisableConfirm] = useState(false);

  const adHocDates = useSelector(state => state.bookingForm.adHocDates);
  const { isHoliday } = useSelector(state => state.bookingForm);

  const dates = useSelector(
    state => state.bookingForm.formData.job?.booked_data?.dates,
  );

  const pay_rates = useSelector(
    state => state.bookingForm.formData.job?.booked_data.pay_rates,
  );
  const placement_purchase_order = useSelector(
    state => state.bookingForm.formData.job?.placement_purchase_order,
  );
  const { shift_types } = useSelector(state => state.referenceBook.bookData);
  const dispatch = useDispatch();

  useEffect(() => {
    if (dates?.length > 0) {
      let modifiedDates = [...dates];
      let dateCounts = modifiedDates.reduce((acc, cur) => {
        acc[cur.date] = (acc[cur.date] || 0) + 1;
        return acc;
      }, {});

      let seenDates = {};

      modifiedDates = modifiedDates.map(el => {
        if (dateCounts[el.date] > 1) {
          seenDates[el.date] = seenDates[el.date] ? seenDates[el.date] + 1 : 1;
          if (seenDates[el.date] === 2) {
            return { ...el, splitDate: el.date, hours: formatTime(el.hours) };
          }
        }
        const newEl = { ...el, hours: formatTime(el.hours) };
        return newEl;
      });
      dispatch(setAdHocDates(modifiedDates));
    }
  }, [dates, dispatch]);

  function formatTime(time) {
    // SECURITY: Handle null/undefined values first to prevent crashes
    // This prevents "Cannot read properties of null (reading 'toString')" errors
    if (time === null || time === undefined || time === '') {
      return ''; // Return empty string for null/undefined/empty values
    }
    
    // SECURITY: Handle different input types correctly
    // If time is already a string in HH:mm format, return as-is
    if (typeof time === 'string' && time.includes(':')) {
      return time;
    }
    
    // If time is a decimal number (e.g., 8.5, 11.25), convert to HH:mm format
    if (typeof time === 'number' && time % 1 !== 0) {
      const hoursInt = Math.floor(time);
      const decimalHours = time - hoursInt;
      const minutes = Math.round(decimalHours * 60);
      const finalMinutes = minutes >= 60 ? 59 : (minutes < 0 ? 0 : minutes);
      const paddedMinutes = finalMinutes < 10 ? '0' + finalMinutes : finalMinutes.toString();
      return `${hoursInt < 10 ? '0' + hoursInt : hoursInt}:${paddedMinutes}`;
    }
    
    // If time is an integer number, treat as old format (e.g., 830, 1230)
    // This handles legacy data that might be stored as integers
    let timeStr = time.toString();
    if (time < 100) {
      timeStr = timeStr.padStart(2, '0') + '00';
    } else {
      timeStr = timeStr.padStart(4, '0');
    }
    return timeStr.slice(0, 2) + ':' + timeStr.slice(2);
  }

  let title = 'Day Shift';

  const options = [
    {
      key: 'all',
      title: 'Copy 1st date data for all picked dates',
    },
  ];

  const handleCheckbox = () => dispatch(setIsHoliday(!isHoliday));

  const handleChange = (day, e, splitDate) => {
    if (e.target.name === 'hours') {
      if (e.target.value === '') {
      } else if (+e.target.value <= 0) return;
    }
    let datesToDispatch = [...adHocDates];
    const filterField = splitDate ? 'splitDate' : 'date';
    datesToDispatch = datesToDispatch.map(el => {
      if (el[filterField] === day && (!el.splitDate || splitDate)) {
        if (e.target.name === 'hours') {
          if (!e.target.value) return el;
          
          // SECURITY: Handle both formatted (HH:mm) and unmasked (1230) values
          // Format unmasked values before parsing with moment
          let hoursValue = e.target.value;
          if (hoursValue && hoursValue.length === 4 && !hoursValue.includes(':')) {
            // Format "1230" to "12:30"
            hoursValue = `${hoursValue.substring(0, 2)}:${hoursValue.substring(2, 4)}`;
          } else if (hoursValue && hoursValue.length === 3 && !hoursValue.includes(':')) {
            // Handle edge case like "830" -> "08:30"
            hoursValue = `0${hoursValue.substring(0, 1)}:${hoursValue.substring(1, 3)}`;
          } else if (hoursValue && hoursValue.length < 3 && !hoursValue.includes(':')) {
            // For partial values (1-2 digits), don't calculate end time yet
            // Just store the value and preserve existing end time
            return {
              ...el,
              [e.target.name]: hoursValue,
              // Preserve existing end time for partial values
              end: el.end,
              end_time: el.end_time,
            };
          }
          
          // Now parse the formatted value
          const parseToHours = moment(hoursValue, momentFormat).format('h');
          if (parseToHours === 'Invalid date') {
            return {
              ...el,
              [e.target.name]: '',
            };
          }
          const getHours = moment(hoursValue, momentFormat).get('h');
          const getMinutes = moment(hoursValue, momentFormat).format('m');

          let endTime = moment(el.start)
            .add(+getHours, 'h')
            .add(+getMinutes, 'm')
            .toDate();

          if (hoursValue === '2400' || hoursValue === '24:00') {
            let tempEndTime = new Date(endTime);
            tempEndTime.setDate(tempEndTime.getDate() + 1);
            endTime = tempEndTime;
          }

          return {
            ...el,
            [e.target.name]: hoursValue, // Store formatted value
            end: endTime,
            end_time: endTime,
          };
        } else if (e.target.name === 'shift_external_id') {
          const sameDates = datesToDispatch.filter(
            date =>
              moment(date[filterField]).format('YYYY-MM-DD') ===
              moment(day).format('YYYY-MM-DD'),
          );

          if (sameDates.length > 1) {
            let shift;
            if (filterField === 'date') {
              const relevantDay = sameDates.find(d => !d.splitDate);
              shift = relevantDay ? relevantDay.shift_external_id : null;
            } else {
              const relevantDay = sameDates.find(d => d.splitDate === day);
              shift = relevantDay ? relevantDay.shift_external_id : null;
            }

            if (+shift === +e.target.value || +e.target.value === 39) {
              setHint(true);
              return el;
            } else if (+e.target.value === 28 || +e.target.value === 44) {
              if (+shift === 28 || +shift === 44) {
                setHint(true);
                return el;
              }
            } else if (
              +e.target.value === 30 ||
              +e.target.value === 40 ||
              +e.target.value === 41 ||
              +e.target.value === 43
            ) {
              if (
                +shift === 30 ||
                +shift === 40 ||
                +shift === 41 ||
                +shift === 43
              ) {
                setHint(true);
                return el;
              }
            }
          }

          const startTime =
            +e.target.value === 28 || +e.target.value === 44
              ? '19:00:00'
              : +e.target.value === 39
              ? '00:00:00'
              : '07:00:00';
          const endTime =
            +e.target.value === 28 || +e.target.value === 44
              ? '07:00:00'
              : +e.target.value === 39
              ? '00:00:00'
              : '19:00:00';
          const defaultHours = +e.target.value === 39 ? '24:00' : '12:00';

          // SECURITY: Preserve existing hours if user has already set custom hours
          // Only use default hours if current hours is empty/null/undefined/0
          // This prevents overwriting user-entered hours when changing shift type
          const preservedHours = 
            el.hours && 
            el.hours !== '' && 
            el.hours !== null && 
            el.hours !== undefined &&
            el.hours !== 0 &&
            el.hours !== '0' &&
            el.hours !== '0:00' &&
            el.hours !== '00:00'
              ? el.hours
              : defaultHours;

          // Calculate end_time based on preserved hours if hours is in HH:mm format
          const startDate = moment(el.start).format('YYYY-MM-DD');
          const endDateStr = 
            +e.target.value === 28 ||
            +e.target.value === 44 ||
            +e.target.value === 39
              ? moment(el.start).add(1, 'day').format('YYYY-MM-DD')
              : moment(el.start).format('YYYY-MM-DD');
          
          let calculatedEndTime = new Date(`${endDateStr}T${endTime}`);
          if (preservedHours !== defaultHours && typeof preservedHours === 'string' && preservedHours.includes(':')) {
            const getHours = moment(preservedHours, momentFormat).get('h');
            const getMinutes = moment(preservedHours, momentFormat).format('m');
            calculatedEndTime = moment(new Date(`${startDate}T${startTime}`))
              .add(+getHours, 'h')
              .add(+getMinutes, 'm')
              .toDate();
            
            // Handle 24:00 case - add one day
            if (preservedHours === '24:00' || preservedHours === '2400') {
              calculatedEndTime = moment(calculatedEndTime).add(1, 'day').toDate();
            }
          }

          return {
            ...el,
            [e.target.name]: e.target.value,
            start: new Date(
              moment(el.start).format('YYYY-MM-DD') + 'T' + startTime,
            ),
            end: calculatedEndTime,
            start_time: new Date(
              moment(el.start).format('YYYY-MM-DD') + 'T' + startTime,
            ),
            end_time: calculatedEndTime,
            hours: preservedHours,
            title:
              e.target.name === 'shift_external_id'
                ? shift_types.find(el => +el.external_id === +e.target.value)
                    .name
                : title,
          };
        } else {
          return {
            ...el,
            [e.target.name]: e.target.value,
          };
        }
      }
      return el;
    });

    if (e.target.name === 'shift_external_id') {
      let newRates = [];
      if (!pay_rates.find(el => +el.shift_external_id === +e.target.value)) {
        const shiftType = shift_types.find(
          el => el.external_id === +e.target.value,
        );

        let currentDay;
        if (filterField === 'date') {
          currentDay = adHocDates.find(el => el.date === day);
        } else {
          currentDay = adHocDates.find(el => el.splitDate === day);
        }

        const currentDays =
          datesToDispatch?.filter(
            el => +el.shift_external_id === +currentDay?.shift_external_id,
          ) ?? [];

        newRates = [
          ...pay_rates,
          {
            new: true,
            id: shiftType.overtime_rate_external_id,
            external_id: shiftType.overtime_rate_external_id,
            shift_external_id: shiftType.external_id,
            name: shiftType.name,
            charge: 0,
            rate: 0,
            fee: 0,
            hidden: false,
          },
        ].filter(el =>
          +el.shift_external_id !== +currentDay.shift_external_id
            ? true
            : +el.shift_external_id === 30 || currentDays.length >= 1,
        );
      } else if (
        pay_rates.find(el => +el.shift_external_id === +e.target.value)
      ) {
        let currentDay;
        if (filterField === 'date') {
          currentDay = adHocDates.find(el => el.date === day);
        } else {
          currentDay = adHocDates.find(el => el.splitDate === day);
        }

        const currentDays =
          datesToDispatch?.filter(
            el => +el.shift_external_id === +currentDay?.shift_external_id,
          ) ?? [];

        newRates = [
          ...pay_rates
            .map(el => {
              if (+el.shift_external_id === +e.target.value) {
                return { ...el, hidden: false };
              }
              return el;
            })
            .filter(el =>
              +el.shift_external_id !== +currentDay.shift_external_id
                ? true
                : +el.shift_external_id === 30 || currentDays.length >= 1,
            ),
        ];
      }

      dispatch(changeRate(newRates));
    }

    // dispatch(setAdHocDates(datesToDispatch));
    checkDates(datesToDispatch);
  };

  const checkDates = datesToDispatch => {
    let modifiedDates = [...datesToDispatch];
    let dateCounts = modifiedDates.reduce((acc, cur) => {
      acc[cur.date] = (acc[cur.date] || 0) + 1;
      return acc;
    }, {});
    setDisableConfirm(false);

    // Note: Date updates will be triggered when user presses Confirm button
    // This ensures dates are only updated when user confirms their selection

    let seenDates = {};
    let tempModifiedDates = [];
    modifiedDates.forEach((el, index) => {
      let newEl = { ...el };
      if (dateCounts[el.date] > 1) {
        seenDates[el.date] = seenDates[el.date] ? seenDates[el.date] + 1 : 1;
        modifiedDates.forEach(other => {
          if (other !== el && other.date === el.date) {
            const start = moment(el.start_time, 'YYYY-MM-DD HH:mm:ss');
            const end = moment(el.end_time, 'YYYY-MM-DD HH:mm:ss');
            const otherStart = moment(other.start_time, 'YYYY-MM-DD HH:mm:ss');
            const otherEnd = moment(other.end_time, 'YYYY-MM-DD HH:mm:ss');
            if (!el.splitDate) {
              if (start.isBefore(otherEnd)) {
                newEl.error = {
                  dateEndError: end.isAfter(otherStart),
                };
                setDisableConfirm(true);
              } else {
                newEl.error = {
                  dateStartError: false,
                };
                setDisableConfirm(false);
              }
            } else {
              if (start.isBefore(otherEnd)) {
                newEl.error = {
                  dateStartError: end.isAfter(otherStart),
                };
                setDisableConfirm(true);
              } else {
                newEl.error = {
                  dateStartError: false,
                };
                setDisableConfirm(false);
              }
            }
          }
        });

        if (seenDates[el.date] === 2) {
          newEl.splitDate = el.date;
        }
      } else {
        newEl.error = {
          dateStartError: false,
          dateEndError: false,
        };
      }
      tempModifiedDates.push(newEl);
    });

    dispatch(setAdHocDates(tempModifiedDates));
  };

  const handleStartTimeChange = (day, value, splitDate) => {
    let datesToDispatch = [...adHocDates];
    const filterField = splitDate ? 'splitDate' : 'date';
    datesToDispatch = datesToDispatch.map(el => {
      if (el[filterField] === day && (!el.splitDate || splitDate)) {
        if (!value) return el;
        const getHoursStart = moment(value, momentFormat).get('h');
        const getMinutesStart = moment(value, momentFormat).format('m');

        // SECURITY: Handle hours correctly - can be decimal (12.5) or string ("12:30")
        // If hours is a decimal, convert to hours and minutes for calculation
        // If hours is a string, parse it with moment
        let hoursToAdd = 0;
        let minutesToAdd = 0;
        
        if (typeof el.hours === 'number') {
          // Decimal hours (e.g., 12.5 = 12 hours 30 minutes)
          hoursToAdd = Math.floor(el.hours);
          minutesToAdd = Math.round((el.hours - hoursToAdd) * 60);
        } else if (typeof el.hours === 'string' && el.hours.includes(':')) {
          // String format (e.g., "12:30")
          const parsed = moment(el.hours, momentFormat);
          if (parsed.isValid()) {
            hoursToAdd = parsed.get('h');
            minutesToAdd = parsed.format('m');
          }
        } else {
          // Fallback: try to parse as string
          const parsed = moment(el.hours, momentFormat);
          if (parsed.isValid()) {
            hoursToAdd = parsed.get('h');
            minutesToAdd = parsed.format('m');
          }
        }

        const startTime = moment(el.start)
          .set('h', getHoursStart)
          .set('m', getMinutesStart)
          .toDate();

        let endTime = moment(startTime)
          .add(+hoursToAdd, 'h')
          .add(+minutesToAdd, 'm')
          .toDate();

        // SECURITY: Preserve hours field when start time changes
        // Hours should not be recalculated from start/end times - preserve user-entered value
        return {
          ...el,
          start: startTime,
          start_time: startTime,
          end: endTime,
          end_time: endTime,
          hours: el.hours, // Explicitly preserve hours to prevent recalculation
        };
      }
      return el;
    });
    // dispatch(setAdHocDates(datesToDispatch));
    checkDates(datesToDispatch);
  };

  const handleCalendarClick = (value, e) => {
    if (
      adHocDates.filter(
        date =>
          moment(date.date).format('YYYY-MM-DD') ===
          moment(value).format('YYYY-MM-DD'),
      ).length > 1
    ) {
      setHint(true);
      return;
    }

    if (
      adHocDates.find(
        date =>
          moment(date.date).format('YYYY-MM-DD') ===
          moment(value).format('YYYY-MM-DD'),
      )
    ) {
      setHint(true);
      return;
    }

    // No date validation needed here - dates will be used to update job start/end dates
    const finalDate = moment(value).toDate();

    const startTime = '07:00:00';
    const endTime = '19:00:00';

    const datesToDispatch = [...adHocDates];

    const getTitle = () => {
      const day = new Date(value).getDay();

      if (day === 6) {
        const unique = [
          ...new Map(
            [
              ...pay_rates,
              {
                charge: 0,
                external_id: 800,
                fee: 0,
                hidden: false,
                id: 800,
                name: 'Saturday',
                new: true,
                rate: 0,
                shift_external_id: 40,
              },
            ].map(item => [item['external_id'], item]),
          ).values(),
        ];

        dispatch(changeRate(unique));

        return {
          shift_external_id: 40,
          title: 'Saturday',
        };
      } else if (day === 0) {
        const unique = [
          ...new Map(
            [
              ...pay_rates,
              {
                charge: 0,
                external_id: 798,
                fee: 0,
                hidden: false,
                id: 798,
                name: 'Sunday & BH',
                new: true,
                rate: 0,
                shift_external_id: 41,
                custom: true,
              },
            ].map(item => [item['external_id'], item]),
          ).values(),
        ];

        dispatch(changeRate(unique));

        return {
          shift_external_id: 41,
          title: 'Sunday & BH',
        };
      }
      return {
        shift_external_id: 30,
        title: 'Day Shift',
      };
    };

    const date = {
      date: finalDate,
      start: new Date(moment(finalDate).format('YYYY-MM-DD') + 'T' + startTime),
      end: new Date(moment(finalDate).format('YYYY-MM-DD') + 'T' + endTime),
      start_time: new Date(
        moment(finalDate).format('YYYY-MM-DD') + 'T' + startTime,
      ),
      end_time: new Date(moment(finalDate).format('YYYY-MM-DD') + 'T' + endTime),
      hours: '12:00',
      purchase_order: null,
      ...getTitle(),
    };
    datesToDispatch.push(date);

    dispatch(setAdHocDates(datesToDispatch));
  };

  const handleDelete = (day, split) => {
    let datesToDispatch = [...adHocDates];
    const filterField = split ? 'splitDate' : 'date';
    const currentDay = datesToDispatch.find(el => el[filterField] === day);

    if (currentDay) {
      const currentDays = adHocDates.filter(
        el => +el.shift_external_id === +currentDay.shift_external_id,
      );
      if (currentDays.length === 1) {
        const newRates = [
          ...pay_rates.filter(
            el =>
              +el.shift_external_id === 30 ||
              +el.shift_external_id !== +currentDay.shift_external_id,
          ),
        ];
        dispatch(changeRate(newRates));
      }

      if (split) {
        const originalEntryIndex = datesToDispatch.findIndex(
          el => el.date === day && !el.splitDate,
        );
        if (originalEntryIndex !== -1) {
          const originalEntry = datesToDispatch[originalEntryIndex];
          let durationToAdd = moment.duration(
            moment(currentDay.end_time).diff(moment(currentDay.start_time)),
          );

          let newEndTime = moment(originalEntry.end_time).add(durationToAdd);
          let additionalHours = durationToAdd.asHours();

          const updatedHours = originalEntry.hours + additionalHours;

          datesToDispatch[originalEntryIndex] = {
            ...originalEntry,
            end_time: new Date(newEndTime),
            end: new Date(newEndTime),
            hours: updatedHours,
          };
        }
      }
    }

    datesToDispatch = datesToDispatch.filter(el => el[filterField] !== day);
    // dispatch(setAdHocDates(datesToDispatch));
    checkDates(datesToDispatch);
  };

  const handlePressSplit = day => {
    let datesToDispatch = [...adHocDates];
    const alreadySplit = datesToDispatch.some(el => el.splitDate === day);

    if (!alreadySplit) {
      const index = datesToDispatch.findIndex(el => el.date === day);
      const currentDay = datesToDispatch[index];

      if (currentDay && index !== -1) {
        const startMoment = moment(
          currentDay.start_time,
          'YYYY-MM-DD HH:mm:ss',
        );
        const endMoment = moment(currentDay.end_time, 'YYYY-MM-DD HH:mm:ss');
        if (endMoment.isBefore(startMoment)) {
          endMoment.add(1, 'day');
        }
        const totalDuration = moment.duration(endMoment.diff(startMoment));
        const halfDuration = totalDuration.asMilliseconds() / 2;
        const middleTime = startMoment
          .clone()
          .add(Math.floor(halfDuration / 3600000) * 3600000, 'milliseconds');
        const halfHours = Math.floor(halfDuration / 3600000);

        const newDay = {
          ...currentDay,
          splitDate: currentDay.date,
          start_time: new Date(middleTime),
          start: new Date(middleTime),
          end_time: new Date(endMoment),
          end: new Date(endMoment),
          hours: halfHours,
        };
        datesToDispatch[index] = {
          ...currentDay,
          end_time: new Date(middleTime),
          end: new Date(middleTime),
          hours: halfHours,
        };

        datesToDispatch.splice(index + 1, 0, newDay);
      }
      dispatch(setAdHocDates(datesToDispatch));
    }
  };

  let datesToShow = [];

  if (adHocDates?.length > 0) {
    datesToShow = [...adHocDates];
    datesToShow.sort((a, b) => a.date - b.date);
  }

  const handleDuplicate = e => {
    if (e.target.value === 'all') {
      const dateToDuplicate = datesToShow[0];
      const duplicatedDates = datesToShow.map((date, index) => {
        const start = moment(date.start)
          .set('h', 0)
          .set('m', 0)
          .set('second', 0);
        const duplicateStart = moment(dateToDuplicate.start)
          .set('h', 0)
          .set('m', 0)
          .set('second', 0);
        const duplicateEnd = moment(dateToDuplicate.end)
          .set('h', 0)
          .set('m', 0)
          .set('second', 0)
          .toDate();

        const diff = moment(duplicateEnd).diff(duplicateStart, 'd');

        const end = moment(date.date)
          .add(diff, 'd')
          .set('h', moment(dateToDuplicate.end).get('h'))
          .set('m', moment(dateToDuplicate.end).get('m'))
          .toDate();

        return {
          ...date,
          shift_external_id: dateToDuplicate.shift_external_id,
          hours: dateToDuplicate.hours,
          start: moment(dateToDuplicate.start)
            .add(moment(start).diff(duplicateStart, 'd'), 'd')
            .toDate(),
          end: end,
          start_time: moment(dateToDuplicate.start)
            .add(moment(start).diff(duplicateStart, 'd'), 'd')
            .toDate(),
          end_time: end,
          purchase_order: dateToDuplicate.purchase_order,
          title: dateToDuplicate.title,
        };
      });
      dispatch(setAdHocDates(duplicatedDates));

      const newRates = [
        ...pay_rates.filter(
          el =>
            +el.shift_external_id === 30 ||
            +el.shift_external_id === +dateToDuplicate.shift_external_id,
        ),
      ];

      dispatch(changeRate(newRates));

      return;
    }
  };

  const parseDate = date => {
    if (typeof date !== 'string') return new Date(date);
    if (isAvailability) {
      return new Date(date);
    } else {
      return new Date(
        new Date(date).getTime() + new Date(date).getTimezoneOffset() * 60000,
      );
    }
  };

  const handleCloseHint = e => {
    e.stopPropagation();
    setHint(false);
  };

  return (
    <div className={css.main}>
      <Calendar onChange={handleCalendarClick} />
      <div className={css.checkboxWrapper}>
        <div className={css.checkboxContainer}>
          <div className={css.color1}></div>
          <span className={css.checkboxText}>Applicant Avaliable</span>
        </div>
        <div className={css.checkboxContainer}>
          <div className={css.color5}></div>
          <span className={css.checkboxText}>Applicant Allocated</span>
        </div>
        <div className={css.checkboxContainer}>
          <input
            className={css.checkbox}
            type="checkbox"
            checked={isHoliday}
            onChange={handleCheckbox}
          />
          <div className={css.color2}></div>
          <span className={css.checkboxText}>Bank Holiday</span>
        </div>
        <div className={css.checkboxContainer}>
          <div className={css.color3}></div>
          <span className={css.checkboxText}>Job Requirement</span>
        </div>
        <div className={css.checkboxContainer}>
          <div className={css.color4}></div>
          <span className={css.checkboxText}>Applicant Not Avaliable</span>
        </div>
      </div>
      <div className={css.grid}>
        <div className={css.header}>
          {hint ? (
            <div className={localCss.hintOverlay} onClick={handleCloseHint}>
              <div className={localCss.hintWrapper}>
                <p className={localCss.hintText}>
                  This date is already picked!
                </p>
              </div>
            </div>
          ) : null}
        </div>
        <span className={css.header}>Shifts</span>
        <span className={css.header}>Hours</span>
        <span className={css.header}>Start Time</span>
        <span className={css.header}>End Time</span>
        <span className={css.header}>Purchase Order</span>
        <span className={css.header}>Ward</span>
        <div className={css.header}>
          {datesToShow.length > 1 ? (
            <Select
              value=""
              size="duplicate"
              options={options}
              onChange={handleDuplicate}
            />
          ) : null}
        </div>
      </div>
      <div className={`${css.grid} ${localCss.scroll}`}>
        {datesToShow?.map((el, i) => (
          <Fragment key={i}>
            {!el.splitDate ? (
              <Input
                name="date"
                type="text"
                custom={localCss.inputDate}
                value={moment(el.date).format('DD.MM.YY ddd') || ''}
                readOnly
              />
            ) : (
              <div className={localCss.inputDate}></div>
            )}
            <Select
              name="shift_external_id"
              value={el.shift_external_id || ''}
              options={shift_types}
              size="x100"
              defaultValue="Day Shift"
              onChange={e => {
                handleChange(el.date, e, el.splitDate);
              }}
              withoutAdaptivePlaceholder
            />
            <IMaskInput
              mask={'00:00'}
              value={(() => {
                if (el?.hours) {
                  if (typeof el.hours === 'number') {
                    // SECURITY: Correctly convert decimal hours back to HH:mm format
                    // Example: 12.5 (12 hours 30 minutes) should become "12:30", not "12:03" or "12:50"
                    const value = el?.hours.toString();
                    
                    // SECURITY: Handle incorrectly stored large numbers (e.g., 1230, 85, 830 instead of 12.5, 8.5)
                    // If number is > 24 and has no decimal, it's likely an unmasked value stored incorrectly
                    // Format it as HH:mm based on number of digits
                    if (el.hours > 24 && !value.includes('.')) {
                      const valueStr = Math.floor(el.hours).toString();
                      if (valueStr.length === 4) {
                        // 4 digits: "1230" -> "12:30"
                        return `${valueStr.substring(0, 2)}:${valueStr.substring(2, 4)}`;
                      } else if (valueStr.length === 3) {
                        // 3 digits: "830" -> "08:30"
                        return `0${valueStr.substring(0, 1)}:${valueStr.substring(1, 3)}`;
                      } else if (valueStr.length === 2) {
                        // 2 digits: "85" -> This is likely 8.5 hours stored incorrectly as 85
                        // Try to interpret as hours:minutes if reasonable, otherwise treat as decimal hours
                        // If > 24, it's definitely wrong - could be 8.5 stored as 85
                        // For now, treat 2-digit numbers > 24 as invalid and return empty
                        // Or we could try to divide by 10: 85 / 10 = 8.5 -> "08:30"
                        const asDecimal = el.hours / 10;
                        if (asDecimal <= 24) {
                          const hours = Math.floor(asDecimal);
                          const minutes = Math.round((asDecimal - hours) * 60);
                          return `${hours < 10 ? '0' + hours : hours}:${minutes < 10 ? '0' + minutes : minutes}`;
                        }
                        // If division by 10 doesn't work, return empty to force user to re-enter
                        return '';
                      }
                    }
                    
                    if (value.includes('.')) {
                      // SECURITY: Convert decimal hours to HH:mm format
                      // Example: 11.25 = 11 hours + 0.25 hours = 11 hours + 15 minutes = "11:15"
                      // Use the actual decimal value, not string manipulation, to avoid precision issues
                      const hoursInt = Math.floor(el.hours);
                      const decimalHours = el.hours - hoursInt;
                      const minutes = Math.round(decimalHours * 60);
                      // Ensure minutes don't exceed 59 (handle rounding errors from floating point)
                      const finalMinutes = minutes >= 60 ? 59 : (minutes < 0 ? 0 : minutes);
                      const paddedMinutes = finalMinutes < 10 ? '0' + finalMinutes : finalMinutes.toString();
                      return `${hoursInt < 10 ? '0' + hoursInt : hoursInt}:${paddedMinutes}`;
                    } else if (value.length === 2 && el.hours <= 24) {
                      // Only treat 2-digit numbers as hours if they're <= 24
                      return `${value}:00`;
                    } else if (value.length === 4 && !value.includes('.')) {
                      // Handle 4-digit numbers that should be formatted: "1230" -> "12:30"
                      return `${value.substring(0, 2)}:${value.substring(2, 4)}`;
                    } else {
                      // For other cases, return empty to force user to re-enter correctly
                      return '';
                    }
                  } else {
                    // If it's already a string, check if it needs formatting
                    const strValue = el?.hours.toString();
                    
                    // SECURITY: Handle incorrectly stored string values
                    // If string is unmasked (e.g., "830", "1230", "85"), format it
                    if (strValue && strValue.length === 4 && !strValue.includes(':')) {
                      return `${strValue.substring(0, 2)}:${strValue.substring(2, 4)}`;
                    } else if (strValue && strValue.length === 3 && !strValue.includes(':')) {
                      return `0${strValue.substring(0, 1)}:${strValue.substring(1, 3)}`;
                    } else if (strValue && strValue.length === 2 && !strValue.includes(':')) {
                      // 2-digit string: could be "85" (8.5 stored incorrectly) or "12" (12:00)
                      const numValue = parseFloat(strValue);
                      if (numValue > 24 && numValue < 100) {
                        // Likely 8.5 stored as "85" - convert to decimal and format
                        const asDecimal = numValue / 10;
                        const hoursInt = Math.floor(asDecimal);
                        const minutes = Math.round((asDecimal - hoursInt) * 60);
                        return `${hoursInt < 10 ? '0' + hoursInt : hoursInt}:${minutes < 10 ? '0' + minutes : minutes}`;
                      } else if (numValue <= 24) {
                        // Valid hours: "12" -> "12:00"
                        return `${strValue}:00`;
                      }
                    }
                    // Otherwise return as-is (should be in HH:mm format)
                    return strValue;
                  }
                } else return '';
              })()}
              unmask={true} // Keep unmask=true to get raw digits
              onAccept={(value, mask) => {
                // SECURITY: Don't process during typing - let user type all digits
                // Processing happens in onBlur to avoid interfering with mask behavior
                // This prevents the mask from inserting colon at wrong position
              }}
              onBlur={e => {
                // SECURITY: Format the value on blur to ensure it's in HH:mm format
                // Get the raw unmasked value from the input
                let rawValue = e.target.value;
                
                // Remove any colons that the mask might have inserted incorrectly
                rawValue = rawValue.replace(/:/g, '');
                
                // Format based on length
                let formattedValue = '';
                if (rawValue && rawValue.length === 4) {
                  // 4 digits: "1230" -> "12:30"
                  formattedValue = `${rawValue.substring(0, 2)}:${rawValue.substring(2, 4)}`;
                } else if (rawValue && rawValue.length === 3) {
                  // 3 digits: "830" -> "08:30" (pad first digit with 0)
                  formattedValue = `0${rawValue.substring(0, 1)}:${rawValue.substring(1, 3)}`;
                } else if (rawValue && rawValue.length === 2) {
                  // 2 digits: "12" -> "12:00"
                  formattedValue = `${rawValue}:00`;
                } else if (rawValue && rawValue.length === 1) {
                  // 1 digit: "1" -> "01:00"
                  formattedValue = `0${rawValue}:00`;
                } else if (rawValue && rawValue.includes(':')) {
                  // Already formatted: use as-is
                  formattedValue = rawValue;
                }
                
                // Validate and format using moment
                if (formattedValue) {
                  const formatted = moment(formattedValue, momentFormat).format(momentFormat);
                  const finalValue = formatted === 'Invalid date'
                    ? ''
                    : formatted === '00:00'
                    ? '24:00'
                    : formatted;
                  
                  // Only update if we have a valid value
                  if (finalValue) {
                handleChange(
                  el.date,
                  {
                    target: {
                      name: 'hours',
                          value: finalValue,
                    },
                  },
                  el.splitDate,
                );
                  }
                }
              }}
              placeholder="8"
              className={inputCss.input}
            />
            <div>
              <DatePicker
                locale="en"
                className={
                  el?.error?.dateStartError ? css.inputTimeRed : css.inputTime
                }
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption="time"
                timeFormat="HH:mm"
                dateFormat="HH:mm"
                placeholderText="07:00"
                onChange={e => {
                  handleStartTimeChange(el.date, e, el.splitDate);
                }}
                selected={(el.start && parseDate(el.start)) || ''}
              />
            </div>
            <div>
              <DatePicker
                locale="en"
                className={
                  el?.error?.dateEndError ? css.inputTimeRed : css.inputTime
                }
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption="time"
                timeFormat="HH:mm"
                dateFormat="HH:mm"
                placeholderText="19:00"
                readOnly
                selected={(el.end && parseDate(el.end)) || ''}
              />
            </div>
            <Input
              name="purchase_order"
              value={el.purchase_order || ''}
              size="x100"
              type="text"
              placeholder="Purchase Order"
              onChange={e => {
                handleChange(el.date, e, el.splitDate);
              }}
              withoutAdaptivePlaceholder
              readOnly={placement_purchase_order ? true : false}
            />
            <Input
              name="ward"
              value={el.ward || ''}
              size="x100"
              type="text"
              placeholder="Ward"
              onChange={e => {
                handleChange(el.date, e, el.splitDate);
              }}
              withoutAdaptivePlaceholder
            />
            <div className={css.deleteContainer}>
              {!el.splitDate ? (
                <img
                  className={css.delete}
                  alt="Delete"
                  src={delete_icon}
                  onClick={handleDelete.bind(this, el.date, false)}
                />
              ) : (
                <img
                  className={css.delete}
                  alt="Delete"
                  src={delete_icon}
                  onClick={handleDelete.bind(this, el.date, true)}
                />
              )}
              {!el.splitDate ? (
                <div
                  className={css.splitContainer}
                  onClick={handlePressSplit.bind(this, el.date)}
                >
                  <img className={css.split} alt="Split" src={split_icon} />
                </div>
              ) : (
                <div className={css.splitContainerEmpty} />
              )}
            </div>
          </Fragment>
        ))}
      </div>
      <JobSectionFooter disableConfirm={disableConfirm} />
    </div>
  );
};

export default AdHoc;
